import { ASPECT_RATIO, MEDIA_RESOURCE_TYPE } from "../components/pages/CreatePage/CreatePageConstants";
import { emojiToBase64 } from "./basicUtilities";
import { containsEmojis, extractEmojis } from "./stringUtilities";

const THUMBNAIL_IMAGE_EXTENSION = 'image/webp';

const THUMBNAIL_LANDSCAPE_SIZE = {
    WIDTH: 640,
    HEIGHT: 360
};

const THUMBNAIL_PORTRAIT_SIZE = {
    WIDTH: 360,
    HEIGHT: 640
};

const THUMBNAIL_SQUARE_SIZE = {
    WIDTH: 360,
    HEIGHT: 360
};

// The function creates image from canvas
// The quality is based on display preview resolution in order to keep it small 
export function getResizedCanvas(canvas, newWidth, newHeight) {
    var tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = newWidth;
    tmpCanvas.height = newHeight;

    var ctx = tmpCanvas.getContext('2d');
    ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, newWidth, newHeight);

    return tmpCanvas;
}

export function getThumbnailSnapshot(canvas, aspectRatio) {
  return new Promise((resolve, reject) => {
    const thumbnailSizes = {
      [ASPECT_RATIO.LANDSCAPE]: THUMBNAIL_LANDSCAPE_SIZE,
      [ASPECT_RATIO.PORTRAIT]: THUMBNAIL_PORTRAIT_SIZE,
      [ASPECT_RATIO.SQUARE]: THUMBNAIL_SQUARE_SIZE,
    };

    const size = thumbnailSizes[aspectRatio];
    const resizedCanvas = getResizedCanvas(canvas, size.WIDTH, size.HEIGHT);

    resizedCanvas.toBlob(blob => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to create thumbnail blob'));
      }
    }, THUMBNAIL_IMAGE_EXTENSION, 1);
  });
}

export async function extractEmojiAndConvertToBase64(textElements, defaultFontSize = 42) {
    let emojis = [];
    const elementsWithEmojis = textElements.filter(element => containsEmojis(element.text));

    if (elementsWithEmojis.length > 0) {
        for (const element of elementsWithEmojis) {
            const extractedEmojis = extractEmojis(element.text);
            const fontSize = element.fontSize !== undefined ? element.fontSize : defaultFontSize;
            extractedEmojis.forEach(emoji => {
                emojis.push({ emoji, fontSize: fontSize });
            });
        }
    }

    return await emojiToBase64(emojis);
}

// Returns a Promise resolved with { width, height } for an image URL
export const getImageDimensions = (url, abortSignal) => new Promise((resolve, reject) => {
	try {
		if (!url) {
			resolve(undefined);
			return;
		}
		if (abortSignal?.aborted) {
			reject(new DOMException('Aborted', 'AbortError'));
			return;
		}

		const img = new Image();
		const cleanup = () => {
			img.removeEventListener('load', onLoad);
			img.removeEventListener('error', onError);
			abortSignal?.removeEventListener?.('abort', onAbort);
		};
		const onLoad = () => {
			const width = img.naturalWidth || img.width;
			const height = img.naturalHeight || img.height;
			cleanup();
			if (width && height) resolve({ width, height }); else resolve(undefined);
		};
		const onError = () => { cleanup(); resolve(undefined); };
		const onAbort = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };

		img.addEventListener('load', onLoad);
		img.addEventListener('error', onError);
		abortSignal?.addEventListener?.('abort', onAbort);
		img.src = url;
	} catch (e) {
		resolve(undefined);
	}
});

// Returns a Promise resolved with { width, height } for a video URL (reads metadata)
export const getVideoDimensions = (url, abortSignal) => new Promise((resolve, reject) => {
	try {
		if (!url) {
			resolve(undefined);
			return;
		}
		if (abortSignal?.aborted) {
			reject(new DOMException('Aborted', 'AbortError'));
			return;
		}

		const video = document.createElement('video');
		video.preload = 'metadata';

		const cleanup = () => {
			video.removeEventListener('loadedmetadata', onLoadedMetadata);
			video.removeEventListener('error', onError);
			abortSignal?.removeEventListener?.('abort', onAbort);
		};
		const onLoadedMetadata = () => {
			const width = video.videoWidth;
			const height = video.videoHeight;
			cleanup();
			if (width && height) resolve({ width, height }); else resolve(undefined);
		};
		const onError = () => { cleanup(); resolve(undefined); };
		const onAbort = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };

		video.addEventListener('loadedmetadata', onLoadedMetadata);
		video.addEventListener('error', onError);
		abortSignal?.addEventListener?.('abort', onAbort);
		video.src = url;
	} catch (e) {
		resolve(undefined);
	}
});

// Wrapper to get media dimensions by type: 'image' | 'video'
export const getMediaDimensions = (url, mediaResourceType, abortSignal) => {
	if (mediaResourceType === MEDIA_RESOURCE_TYPE.IMAGE) return getImageDimensions(url, abortSignal);
	if (mediaResourceType === MEDIA_RESOURCE_TYPE.VIDEO) return getVideoDimensions(url, abortSignal);
	return Promise.resolve(undefined);
};